/**
 * SAD-948 — "what may this viewer administer?" in one place.
 *
 * Company administration that is NOT access administration rides two area
 * capabilities, granted through roles on the TENANT ROOT scope node:
 *   - area:it_admin         → the tenant's sign-in configuration (SSO and the
 *                             SharePoint OAuth app)
 *   - area:company_settings → org structure, government orgs, audit
 *                             templates, notification status, usage
 * plus area:registries for the shared catalogs. The servers resolve each one
 * against the tenant root; this asks the same resolver the same question, via
 * useView's granted(). Admin and internal hold the first two by role.
 *
 * Fail-closed: while the resolve is loading or failed, no area is held. The
 * servers re-check every call, so this only decides what to show.
 */ import { useAuth } from '../../contexts/authContext';
import { useView } from './useView';
/** The tenant root scope node, as the resolver addresses it. */ export function tenantRootRef(tenantId, kind = 'program') {
    return {
        kind,
        id: tenantId,
        objectType: 'identity.tenants',
        objectId: tenantId
    };
}
export function useTenantAreas() {
    const { user } = useAuth();
    const role = user?.role?.toLowerCase() ?? '';
    const isAdmin = role === 'admin';
    const isInternal = role === 'internal';
    const tenantId = user?.tenant_id ?? '';
    const byRole = isAdmin || isInternal;
    // Each area is asked as ITS OWN action, the question the servers ask. Not
    // `read`: the IT Admin and Company Settings Admin roles are area-only, so a
    // read resolve is (rightly) denied for them and would hide the grant.
    // IT Admin rides the sign-in config kind, where its Cerbos rule lives.
    const settings = useView(tenantRootRef(tenantId), {
        action: 'area:company_settings',
        enabled: !!tenantId && !byRole
    });
    const it = useView(tenantRootRef(tenantId, 'sharepoint_oauth_settings'), {
        action: 'read',
        enabled: !!tenantId && !byRole
    });
    const reg = useView(tenantRootRef(tenantId), {
        action: 'area:registries',
        enabled: !!tenantId
    });
    const itAdmin = it.granted('area:it_admin');
    const companySettings = settings.granted('area:company_settings');
    return {
        isAdmin,
        isInternal,
        itAdmin,
        companySettings,
        registries: reg.granted('area:registries'),
        canManageSignIn: byRole || itAdmin,
        canManageCompanySettings: byRole || companySettings,
        isLoading: settings.isLoading || it.isLoading || reg.isLoading
    };
}
